import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { DateTime } from "luxon";
import LoadingButton from "@mui/lab/LoadingButton";
import type { DialogProps } from "../misc/DialogProps";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { PdfViewer } from "../misc/PdfViewer";
import { studentDocumentsProvider } from "../providers/documentsProvider";
import type { Student } from "../providers/studentsProvider";
import type { DrivingSchoolAutoForm } from "../providers/autoFormsProvider";
import { useBookedQuotes } from "../hooks/useBookedQuotes";
import { runSafelyWithServerClient } from "../api/server.api";

export function AddAutoFormDocumentDialog(props: { student: Student; autoForm: DrivingSchoolAutoForm } & DialogProps) {
  const { student, autoForm, open, onClose } = props;
  const { selectedBookedQuote, bookedQuotes } = useBookedQuotes(student);
  const [selectedQuoteId, setSelectedQuoteId] = useState(selectedBookedQuote.id);
  const [pdf, setPdf] = useState<Blob | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    if (!open || !selectedQuoteId) return;
    let cancelled = false;
    setIsLoading(true);
    setPdf(null);
    void runSafelyWithServerClient(
      async (serverClient) => {
        const blob = (await serverClient.fillOutForm({
          params: { id: student.id, formId: autoForm.id },
          queries: { withoutBackground: false, quoteId: selectedQuoteId },
          responseType: "blob",
        })) as unknown as Blob;
        if (!cancelled) {
          setPdf(blob);
          setIsLoading(false);
        }
      },
      {
        errorMessage: "Fehler beim Laden des Formulars.",
        successMessage: "",
        onError: () => {
          if (cancelled) {
            return;
          }
          setPdf(null);
          setIsLoading(false);
        },
      },
    );
    return () => {
      cancelled = true;
    };
  }, [open, selectedQuoteId, student.id, autoForm.id]);

  const addPdfToDocuments = async () => {
    if (!pdf) return;
    setIsAdding(true);
    await runSafely(
      async () => {
        const file = new File(
          [pdf],
          `${autoForm.name}_${student.name}_${DateTime.now().toFormat("yyyyMMdd_HHmmss")}.pdf`,
          { type: "application/pdf" },
        );
        await studentDocumentsProvider.create("studentDocuments", {
          data: { recordId: student.id, files: [{ rawFile: file }] },
        });
        onClose();
      },
      {
        successMessage: `Das generierte Auto-Formular ${autoForm.name} wurde den Dokumenten des Studenten hinzugefügt.`,
        errorMessage: "Fehler beim Hinzufügen des Dokuments.",
      },
    );
    setIsAdding(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={pdf ? "lg" : "sm"} fullWidth>
      <DialogTitle>{autoForm.name}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent sx={{ paddingBottom: pdf ? 0 : undefined, overflowY: pdf ? "hidden" : undefined }}>
        {bookedQuotes.length > 1 && (
          <FormControl fullWidth sx={{ my: 2 }} size="small">
            <InputLabel>Paket</InputLabel>
            <Select label="Paket" value={selectedQuoteId} onChange={(e) => setSelectedQuoteId(e.target.value)}>
              {bookedQuotes.map((q) => (
                <MenuItem key={q.id} value={q.id}>
                  {q.bundleName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {pdf && <PdfViewer pdf={pdf} sx={{ width: "100%", height: "70vh" }} />}
      </DialogContent>
      <DialogActions sx={{ marginTop: pdf ? "30px" : undefined }}>
        <LoadingButton variant="contained" loading={isAdding} disabled={!pdf || isLoading} onClick={addPdfToDocuments}>
          {`${autoForm.name} erstellen`}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
